import { createRouter, createWebHistory } from 'vue-router';

import { useStepStore, useSubscriptionStore } from '@/stores';

import type { SubscriptionRouteName } from '@/types/router.type';

import BillingInformationsPage from '@/pages/BillingInformations.page.vue';
import BillingModePage from '@/pages/BillingMode.page.vue';
import CashbackActivationPage from '@/pages/CashbackActivation.page.vue';
import ContractNumberRegistrationPage from '@/pages/ContractNumberRegistration.page.vue';
import ContractorInformationPage from '@/pages/ContractorInformation.page.vue';
import ContractSignaturePage from '@/pages/ContractSignature.page.vue';
import ContractSignedPage from '@/pages/ContractSigned.page.vue';
import ElectricityConsumptionPage from '@/pages/ElectricityConsumption.page.vue';
import ElectricitySupplierPage from '@/pages/ElectricitySupplier.page.vue';
import EquipmentsDeclarationPage from '@/pages/EquipmentsDeclaration.page.vue';
import ImpossibleSubscriptionPage from '@/pages/ImpossibleSubscription.page.vue';
import InformationsSummaryPage from '@/pages/InformationsSummary.page.vue';
import InstallationAddressPage from '@/pages/InstallationAddress.page.vue';
import InstallerInformationPage from '@/pages/InstallerInformation.page.vue';
import PdlRegistrationPage from '@/pages/PdlRegistration.page.vue';
import PhysicalBatteryPage from '@/pages/PhysicalBattery.page.vue';
import SolarInstallationPage from '@/pages/SolarInstallation.page.vue';
import SolarProductionStoragePage from '@/pages/SolarProductionStorage.page.vue';
import SubscriptionFullWidthLayout from '@/pages/SubscriptionFullWidthLayout.page.vue';
import SubscriptionLayoutPage from '@/pages/SubscriptionLayout.page.vue';

import { ESubscriptionRouteName } from '@/constants/router.constants';

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_APPLICATION_BASE_URL),
  routes: [
    {
      path: '/:pathMatch(.*)*',
      redirect: to => ({ path: '/', query: to.query }),
    },
    {
      path: '/',
      component: SubscriptionLayoutPage,
      redirect: to => ({ name: ESubscriptionRouteName.PDL, query: to.query }),
      children: [
        {
          path: 'pdl',
          name: ESubscriptionRouteName.PDL,
          component: PdlRegistrationPage,
        },
        {
          path: 'contract-number',
          name: ESubscriptionRouteName.CONTRACT_NUMBER_REGISTRATION,
          component: ContractNumberRegistrationPage,
        },
        {
          path: 'installation-address',
          name: ESubscriptionRouteName.INSTALLATION_ADDRESS,
          component: InstallationAddressPage,
        },
        {
          path: 'equipments',
          name: ESubscriptionRouteName.EQUIPMENTS,
          component: EquipmentsDeclarationPage,
        },
        {
          path: 'solar-installation',
          name: ESubscriptionRouteName.SOLAR_INSTALLATION,
          component: SolarInstallationPage,
        },
        {
          path: 'installer-information',
          name: ESubscriptionRouteName.INSTALLER_INFORMATIONS,
          component: InstallerInformationPage,
        },
        {
          path: 'impossible-subscription',
          name: ESubscriptionRouteName.IMPOSSIBLE_SUBSCRIPTION,
          component: ImpossibleSubscriptionPage,
        },
        {
          path: 'physical-battery',
          name: ESubscriptionRouteName.PHYSICAL_BATTERY,
          component: PhysicalBatteryPage,
        },
        {
          path: 'solar-production-storage',
          name: ESubscriptionRouteName.SOLAR_PRODUCTION_STORAGE,
          component: SolarProductionStoragePage,
        },
        {
          path: 'cashback-activation',
          name: ESubscriptionRouteName.CASHBACK_ACTIVATION,
          component: CashbackActivationPage,
        },
        {
          path: 'electricity-consumption',
          name: ESubscriptionRouteName.ELECTRICITY_CONSUMPTION,
          component: ElectricityConsumptionPage,
        },
        {
          path: 'electricity-supplier',
          name: ESubscriptionRouteName.ELECTRICITY_SUPPLIER,
          component: ElectricitySupplierPage,
        },
        {
          path: 'billing-mode',
          name: ESubscriptionRouteName.BILLING_MODE,
          component: BillingModePage,
        },
        {
          path: 'contractor-informations',
          name: ESubscriptionRouteName.CONTRACTOR_INFORMATIONS,
          component: ContractorInformationPage,
        },
        {
          path: 'billing-informations',
          name: ESubscriptionRouteName.BILLING_INFORMATIONS,
          component: BillingInformationsPage,
        },
        {
          path: 'informations-summary',
          name: ESubscriptionRouteName.INFORMATIONS_SUMMARY,
          component: InformationsSummaryPage,
        },
        {
          path: 'contract-signed',
          name: ESubscriptionRouteName.CONTRACT_SIGNED,
          component: ContractSignedPage,
        },
      ],
    },
    {
      path: '/',
      component: SubscriptionFullWidthLayout,
      children: [
        {
          path: 'contract-signature',
          name: ESubscriptionRouteName.CONTRACT_SIGNATURE,
          component: ContractSignaturePage,
        },
      ],
    },
  ],
});

router.beforeEach((to, from, next) => {
  const stepStore = useStepStore();
  const subscriptionStore = useSubscriptionStore();

  const currentStepName: SubscriptionRouteName = to.name as SubscriptionRouteName;

  if (Object.values(ESubscriptionRouteName).includes(currentStepName)) {
    stepStore.setCurrentStep(currentStepName);
  }

  const isResubscription = subscriptionStore.pdlNumber && subscriptionStore.getIsResubscription;

  const shouldRedirectFromOtherPage =
    to.name !== ESubscriptionRouteName.PDL && !subscriptionStore.subscriptionIdFromStorage;

  const shouldRedirectToPdlPage =
    to.name === ESubscriptionRouteName.CONTRACT_NUMBER_REGISTRATION
      ? !isResubscription
      : shouldRedirectFromOtherPage;

  if (shouldRedirectToPdlPage) {
    return next({ name: ESubscriptionRouteName.PDL, query: to.query, replace: true });
  }

  return next();
});
export default router;
